import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { format, isValid, parse, parseISO } from "date-fns";
import { useToast } from "../contexts/ToastContext";

const EMPTY_LABEL = "Не указано";

const QUALITY_LABEL_CLASS = {
  высокий: "badge badge-high",
  хороший: "badge badge-good",
  средний: "badge badge-medium",
  низкий: "badge badge-low",
  new: "badge bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200",
};

function normalizeValue(value, fallback = EMPTY_LABEL) {
  if (value === 0) return "0";
  if (value == null) return fallback;
  const normalized = String(value).trim();
  return normalized || fallback;
}

function parseDateValue(value) {
  if (!value) return null;
  if (value instanceof Date) return value;

  const raw = String(value).trim();
  const iso = parseISO(raw);
  if (isValid(iso)) return iso;

  const knownFormats = [
    "dd.MM.yyyy HH:mm:ss",
    "dd.MM.yyyy HH:mm",
    "yyyy-MM-dd HH:mm:ss",
    "yyyy-MM-dd'T'HH:mm:ss",
    "yyyy-MM-dd",
  ];

  for (const dateFormat of knownFormats) {
    const parsed = parse(raw, dateFormat, new Date());
    if (isValid(parsed)) return parsed;
  }

  const fallback = new Date(raw);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
}

function formatDate(value) {
  const date = parseDateValue(value);
  if (!date) return normalizeValue(value);
  return format(date, "dd.MM.yyyy HH:mm");
}

function formatDateOnly(value) {
  const date = parseDateValue(value);
  if (!date) return normalizeValue(value);
  return format(date, "dd.MM.yyyy");
}

function toTimestamp(value) {
  const date = parseDateValue(value);
  return date ? date.getTime() : 0;
}

function getMeetingStatus(value) {
  const normalized = normalizeValue(value, "").toLowerCase();
  if (["да", "yes", "true", "1", "y"].includes(normalized)) return "Да";
  if (["нет", "no", "false", "0", "n"].includes(normalized)) return "Нет";
  return EMPTY_LABEL;
}

function MeetingStatus({ value }) {
  const status = getMeetingStatus(value);

  if (status === "Да") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-sm font-bold text-emerald-700 ring-1 ring-emerald-200" title="Да" aria-label="Встреча: да">
        ✓
      </span>
    );
  }

  if (status === "Нет") {
    return (
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-rose-50 text-sm font-bold text-rose-700 ring-1 ring-rose-200" title="Нет" aria-label="Встреча: нет">
        ×
      </span>
    );
  }

  return (
    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-amber-50 text-sm font-bold text-amber-700 ring-1 ring-amber-200" title={EMPTY_LABEL} aria-label="Встреча: не указано">
      −
    </span>
  );
}

function getQualityValue(lead) {
  const rawQuality = String(lead.client_quality_raw ?? lead.client_quality ?? "").trim().toLowerCase();
  const bucketRaw = String(lead.client_quality_bucket || "").trim();
  if (lead.is_new_lead === true || rawQuality === "new" || bucketRaw.toLowerCase() === "new") {
    return "New";
  }

  const bucket = normalizeValue(bucketRaw);
  const score = normalizeValue(lead.client_quality);
  return `${bucket} (${score})`;
}

function renderTruncated(value, maxWidth = "max-w-[220px]") {
  const normalized = normalizeValue(value);
  return (
    <span className={`block truncate ${maxWidth}`} title={normalized}>
      {normalized}
    </span>
  );
}

const TABLE_COLUMNS = [
  {
    key: "id",
    label: "ID",
    cellClassName: "min-w-[170px]",
    getValue: (lead) => normalizeValue(lead.id),
    render: (lead) => renderTruncated(lead.id, "max-w-[165px]"),
  },
  {
    key: "datetime",
    label: "Дата",
    cellClassName: "min-w-[155px]",
    getValue: (lead) => formatDate(lead.datetime),
    getFilterValue: (lead) => formatDateOnly(lead.datetime),
    getSortValue: (lead) => toTimestamp(lead.datetime),
  },
  {
    key: "name",
    label: "Имя",
    cellClassName: "min-w-[150px]",
    getValue: (lead) => normalizeValue(lead.name),
    render: (lead) => renderTruncated(lead.name, "max-w-[170px]"),
  },
  {
    key: "city",
    label: "Город",
    cellClassName: "min-w-[135px]",
    getValue: (lead) => normalizeValue(lead.city),
  },
  {
    key: "selected_car",
    label: "Модель",
    cellClassName: "min-w-[150px]",
    getValue: (lead) => normalizeValue(lead.selected_car),
    render: (lead) => renderTruncated(lead.selected_car, "max-w-[170px]"),
  },
  {
    key: "client_quality",
    label: "Качество",
    cellClassName: "min-w-[150px]",
    getValue: getQualityValue,
    getFilterValue: (lead) => normalizeValue(lead.client_quality_bucket),
    getSortValue: (lead) => Number(lead.client_quality) || 0,
    render: (lead) => {
      const qualityClass =
        QUALITY_LABEL_CLASS[lead.client_quality_bucket] ||
        QUALITY_LABEL_CLASS[String(lead.client_quality_bucket || "").toLowerCase()] ||
        "badge bg-slate-100 text-slate-700";

      return <span className={qualityClass}>{getQualityValue(lead)}</span>;
    },
  },
  {
    key: "messenger",
    label: "Канал",
    cellClassName: "min-w-[145px]",
    getValue: (lead) => normalizeValue(lead.messenger),
    render: (lead) => renderTruncated(lead.messenger, "max-w-[155px]"),
  },
  {
    key: "dealer_center",
    label: "Дилерский центр",
    cellClassName: "min-w-[190px]",
    getValue: (lead) => normalizeValue(lead.dealer_center),
    render: (lead) => renderTruncated(lead.dealer_center, "max-w-[220px]"),
  },
  {
    key: "Dialog_repeat",
    label: "Повторы",
    cellClassName: "min-w-[115px] text-center",
    getValue: (lead) => Number(lead.Dialog_repeat ?? lead.dialog_repeat ?? lead.repeated_appeal ?? 0),
    getSortValue: (lead) => Number(lead.Dialog_repeat ?? lead.dialog_repeat ?? lead.repeated_appeal ?? 0),
  },
  {
    key: "meeting",
    label: "Встреча",
    cellClassName: "min-w-[110px]",
    getValue: (lead) => getMeetingStatus(lead.meeting),
    render: (lead) => (
      <span className="flex justify-center">
        <MeetingStatus value={lead.meeting} />
      </span>
    ),
  },
  {
    key: "source_system",
    label: "Система",
    cellClassName: "min-w-[130px]",
    getValue: (lead) => normalizeValue(lead.source_system),
  },
  {
    key: "platform_user_id",
    label: "ID платформы",
    cellClassName: "min-w-[155px]",
    getValue: (lead) => normalizeValue(lead.platform_user_id),
    render: (lead) => renderTruncated(lead.platform_user_id, "max-w-[170px]"),
  },
  {
    key: "dialog_link",
    label: "CRM / диалог",
    cellClassName: "min-w-[130px]",
    getValue: (lead) => normalizeValue(lead.dialog_link),
    getFilterValue: (lead) => (lead.dialog_link ? "Есть ссылка" : EMPTY_LABEL),
    render: (lead) =>
      lead.dialog_link ? (
        <a href={lead.dialog_link} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline dark:text-blue-300">
          Открыть
        </a>
      ) : (
        <span className="text-slate-400">{EMPTY_LABEL}</span>
      ),
  },
  {
    key: "summary_dialog",
    label: "Резюме",
    cellClassName: "min-w-[160px]",
    getValue: (lead) => normalizeValue(lead.summary_dialog),
    render: (lead, { onOpenSummary }) => {
      const summary = normalizeValue(lead.summary_dialog);

      if (summary === EMPTY_LABEL) {
        return <span className="text-slate-400">{EMPTY_LABEL}</span>;
      }

      return (
        <button
          type="button"
          onClick={() => onOpenSummary(lead)}
          className="inline-flex h-8 items-center rounded-md border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
        >
          Смотреть
        </button>
      );
    },
  },
];

function getColumnFilterValue(column, lead) {
  return column.getFilterValue ? column.getFilterValue(lead) : column.getValue(lead);
}

function compareFilterOptions(a, b) {
  if (a.value === EMPTY_LABEL) return 1;
  if (b.value === EMPTY_LABEL) return -1;
  return String(a.value).localeCompare(String(b.value), "ru");
}

function getLeadKey(lead, index) {
  return lead.id || lead.platform_user_id || lead.dialog_link || `lead-${index}`;
}

const LeadsTable = ({
  leads,
  total,
  limit,
  offset,
  onPageChange,
  onPageSizeChange,
  onViewLead,
  loading,
}) => {
  const [sortField, setSortField] = useState("datetime");
  const [sortDirection, setSortDirection] = useState("desc");
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterMenuPosition, setFilterMenuPosition] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [dateRangeFilter, setDateRangeFilter] = useState({ from: "", to: "" });
  const [filterSearch, setFilterSearch] = useState({});
  const [summaryLead, setSummaryLead] = useState(null);
  const tableRef = useRef(null);
  const { addToast } = useToast();
  const currentPage = Math.floor(offset / limit) + 1;
  const serverTotal = Number(total || 0);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tableRef.current && !tableRef.current.contains(event.target)) {
        setOpenFilterKey(null);
        setFilterMenuPosition(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filterOptionsByColumn = useMemo(() => {
    const source = Array.isArray(leads) ? leads : [];

    return TABLE_COLUMNS.reduce((acc, column) => {
      const counts = new Map();
      source.forEach((lead) => {
        const value = getColumnFilterValue(column, lead);
        counts.set(value, (counts.get(value) || 0) + 1);
      });

      acc[column.key] = Array.from(counts.entries())
        .map(([value, count]) => ({ value, count }))
        .sort(compareFilterOptions);

      return acc;
    }, {});
  }, [leads]);

  const { visibleLeads, filteredPageTotal, sortedLeads } = useMemo(() => {
    const source = Array.isArray(leads) ? leads : [];
    const fromTs = dateRangeFilter.from ? toTimestamp(`${dateRangeFilter.from}T00:00:00`) : null;
    const toTs = dateRangeFilter.to ? toTimestamp(`${dateRangeFilter.to}T23:59:59`) : null;

    const filtered = source.filter((lead) =>
      TABLE_COLUMNS.every((column) => {
        if (column.key === "datetime" && (fromTs || toTs)) {
          const leadTs = toTimestamp(lead.datetime);
          if (!leadTs) return false;
          if (fromTs && leadTs < fromTs) return false;
          if (toTs && leadTs > toTs) return false;
        }

        const selectedValues = columnFilters[column.key] || [];
        if (selectedValues.length === 0) return true;
        return selectedValues.includes(getColumnFilterValue(column, lead));
      })
    );

    const sortColumn = TABLE_COLUMNS.find((column) => column.key === sortField) || TABLE_COLUMNS[0];
    const sorted = [...filtered].sort((a, b) => {
      const dir = sortDirection === "asc" ? 1 : -1;
      const av = sortColumn.getSortValue ? sortColumn.getSortValue(a) : sortColumn.getValue(a);
      const bv = sortColumn.getSortValue ? sortColumn.getSortValue(b) : sortColumn.getValue(b);

      if (av === bv) return 0;
      if (typeof av === "number" && typeof bv === "number") return av > bv ? dir : -dir;
      return String(av).localeCompare(String(bv), "ru") * dir;
    });

    return { visibleLeads: sorted, filteredPageTotal: filtered.length, sortedLeads: sorted };
  }, [leads, columnFilters, dateRangeFilter, sortField, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(serverTotal / limit));
  const activeFilterCount = Object.values(columnFilters).reduce((sum, values) => sum + values.length, 0) +
    (dateRangeFilter.from ? 1 : 0) +
    (dateRangeFilter.to ? 1 : 0);
  const showEmptyState = !loading && visibleLeads.length === 0;

  const handleLimitChange = useCallback((event) => {
    onPageSizeChange(Number(event.target.value) || 25);
  }, [onPageSizeChange]);

  const goToPage = useCallback((page) => {
    if (page < 1 || page > totalPages) return;
    onPageChange((page - 1) * limit);
  }, [limit, onPageChange, totalPages]);

  const applySort = useCallback((field, direction) => {
    setSortField(field);
    setSortDirection(direction);
  }, []);

  const toggleFilterValue = useCallback((columnKey, value) => {
    setColumnFilters((prev) => {
      const current = prev[columnKey] || [];
      const nextValues = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      const next = { ...prev };

      if (nextValues.length > 0) next[columnKey] = nextValues;
      else delete next[columnKey];

      return next;
    });
  }, []);

  const clearColumnFilter = useCallback((columnKey) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      delete next[columnKey];
      return next;
    });
    if (columnKey === "datetime") setDateRangeFilter({ from: "", to: "" });
  }, []);

  const openColumnFilter = useCallback((event, columnKey) => {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 288;
    const estimatedMenuHeight = 420;
    const viewportPadding = 12;
    const left = Math.min(Math.max(rect.left, viewportPadding), window.innerWidth - menuWidth - viewportPadding);
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const top = spaceBelow >= estimatedMenuHeight
      ? rect.bottom + 6
      : Math.max(viewportPadding, rect.top - estimatedMenuHeight - 6);

    setFilterMenuPosition({ left, top });
    setOpenFilterKey((current) => (current === columnKey ? null : columnKey));
  }, []);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
    setDateRangeFilter({ from: "", to: "" });
    setFilterSearch({});
  }, []);

  const exportToCsv = useCallback(() => {
    if (sortedLeads.length === 0) {
      addToast("Нет данных для экспорта.", "error");
      return;
    }

    const header = TABLE_COLUMNS.map((column) => column.label);
    const csvLines = [
      header.join(","),
      ...sortedLeads.map((lead) =>
        TABLE_COLUMNS
          .map((column) => {
            const value = column.getValue(lead);
            return `"${String(value ?? "").replace(/"/g, '""')}"`;
          })
          .join(",")
      ),
    ];

    const blob = new Blob([`\uFEFF${csvLines.join("\n")}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    addToast("CSV успешно скачан.", "success");
  }, [addToast, sortedLeads]);

  const renderFilterMenu = () => {
    if (!openFilterKey || !filterMenuPosition) return null;

    const column = TABLE_COLUMNS.find((item) => item.key === openFilterKey);
    if (!column) return null;

    const isDateColumn = column.key === "datetime";
    const selectedValues = columnFilters[column.key] || [];
    const hasDateRange = column.key === "datetime" && (dateRangeFilter.from || dateRangeFilter.to);
    const options = filterOptionsByColumn[column.key] || [];
    const search = filterSearch[column.key] || "";
    const visibleOptions = search
      ? options.filter((option) => String(option.value).toLowerCase().includes(search.toLowerCase()))
      : options;

    return (
      <div
        className="fixed z-[9999] w-72 rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-700 shadow-2xl dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        style={{
          left: filterMenuPosition.left,
          top: filterMenuPosition.top,
          maxHeight: `calc(100vh - ${filterMenuPosition.top + 12}px)`,
        }}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => applySort(column.key, "asc")}
            className="rounded-md border border-slate-200 px-2 py-1 text-left hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Сорт. ↑
          </button>
          <button
            type="button"
            onClick={() => applySort(column.key, "desc")}
            className="rounded-md border border-slate-200 px-2 py-1 text-left hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800"
          >
            Сорт. ↓
          </button>
        </div>

        {!isDateColumn ? (
          <input
            type="text"
            value={search}
            onChange={(event) => setFilterSearch((prev) => ({ ...prev, [column.key]: event.target.value }))}
            placeholder="Найти значение..."
            className="mt-3 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800"
          />
        ) : (
          <div className="mt-3 rounded-md border border-slate-200 p-2 dark:border-slate-700">
            <p className="mb-2 text-[11px] font-semibold uppercase text-slate-500 dark:text-slate-400">Диапазон дат</p>
            <div className="grid gap-2">
              <label className="text-[11px] text-slate-500 dark:text-slate-400">
                С даты
                <input
                  type="date"
                  value={dateRangeFilter.from}
                  onChange={(event) => setDateRangeFilter((prev) => ({ ...prev, from: event.target.value }))}
                  max={dateRangeFilter.to || undefined}
                  className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
              </label>
              <label className="text-[11px] text-slate-500 dark:text-slate-400">
                По дату
                <input
                  type="date"
                  value={dateRangeFilter.to}
                  onChange={(event) => setDateRangeFilter((prev) => ({ ...prev, to: event.target.value }))}
                  min={dateRangeFilter.from || undefined}
                  className="mt-1 w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100"
                />
              </label>
            </div>
          </div>
        )}

        {!isDateColumn && (
          <div className="mt-2 max-h-64 overflow-y-auto pr-1">
            {visibleOptions.length > 0 ? (
              visibleOptions.map((option) => (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 hover:bg-slate-50 dark:hover:bg-slate-800"
                  title={String(option.value)}
                >
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => toggleFilterValue(column.key, option.value)}
                    className="h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="min-w-0 flex-1 truncate">{option.value}</span>
                  <span className="text-[10px] text-slate-400">{option.count}</span>
                </label>
              ))
            ) : (
              <p className="px-1 py-3 text-slate-400">Нет значений</p>
            )}
          </div>
        )}

        <button
          type="button"
          onClick={() => clearColumnFilter(column.key)}
          className="mt-2 w-full rounded-md border border-slate-200 px-2 py-1.5 text-left text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          disabled={selectedValues.length === 0 && !hasDateRange}
        >
          Сбросить колонку
        </button>
      </div>
    );
  };

  const renderColumnFilter = (column) => {
    const selectedValues = columnFilters[column.key] || [];
    const dateRangeCount = column.key === "datetime"
      ? Number(Boolean(dateRangeFilter.from)) + Number(Boolean(dateRangeFilter.to))
      : 0;
    const columnFilterCount = selectedValues.length + dateRangeCount;

    return (
      <th key={column.key} className="px-3 py-3 text-left align-top">
        <button
          type="button"
          onClick={(event) => openColumnFilter(event, column.key)}
          className={`inline-flex items-center gap-1 rounded px-1 py-0.5 text-left hover:bg-slate-100 dark:hover:bg-slate-700 ${
            columnFilterCount > 0 ? "text-blue-700 dark:text-blue-300" : ""
          }`}
        >
          <span>{column.label}</span>
          <span className="text-[10px] text-slate-400">{sortField === column.key ? (sortDirection === "desc" ? "↓" : "↑") : "▾"}</span>
          {columnFilterCount > 0 ? (
            <span className="rounded-full bg-blue-100 px-1.5 py-0.5 text-[10px] text-blue-700 dark:bg-blue-900/50 dark:text-blue-200">
              {columnFilterCount}
            </span>
          ) : null}
        </button>
      </th>
    );
  };

  return (
    <section ref={tableRef} className="card p-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-100">Лиды</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Фильтры и сортировка доступны в заголовках колонок.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {activeFilterCount > 0 ? (
            <button
              type="button"
              onClick={clearAllFilters}
              className="h-9 rounded-md border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
            >
              Сбросить фильтры ({activeFilterCount})
            </button>
          ) : null}
          <button
            type="button"
            onClick={exportToCsv}
            className="h-9 rounded-md border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
          >
            Экспорт CSV
          </button>
        </div>
      </div>

      {showEmptyState ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-center dark:border-slate-700 dark:bg-slate-900/40">
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-200">Ничего не найдено</h3>
          <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Снимите часть фильтров в заголовках колонок.</p>
        </div>
      ) : (
        <div className="overflow-x-auto overflow-y-auto">
          <table className="min-w-[1840px] text-sm">
            <thead className="table-header sticky top-0 z-20">
              <tr>
                {TABLE_COLUMNS.map(renderColumnFilter)}
                <th className="sticky right-0 z-30 min-w-[120px] bg-slate-50 px-3 py-3 text-right dark:bg-slate-800">
                  Действия
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: Math.min(limit, 8) }).map((_, index) => (
                  <tr key={`skeleton-${index}`} className="table-row">
                    <td colSpan={TABLE_COLUMNS.length + 1} className="px-3 py-3">
                      <div className="h-4 w-full animate-pulse rounded bg-slate-200 dark:bg-slate-700" />
                    </td>
                  </tr>
                ))
              ) : (
                visibleLeads.map((lead, index) => {
                  const qualityStripeClass =
                    lead.client_quality_bucket === "высокий"
                      ? "border-l-4 border-emerald-500"
                      : lead.client_quality_bucket === "хороший"
                      ? "border-l-4 border-sky-500"
                      : lead.client_quality_bucket === "средний"
                      ? "border-l-4 border-amber-500"
                      : lead.is_new_lead
                      ? "border-l-4 border-slate-300"
                      : "border-l-4 border-rose-500";

                  return (
                    <tr key={getLeadKey(lead, index)} className={`table-row ${qualityStripeClass}`}>
                      {TABLE_COLUMNS.map((column) => (
                        <td
                          key={`${getLeadKey(lead, index)}-${column.key}`}
                          className={`px-3 py-3 text-sm text-slate-700 dark:text-slate-300 ${column.cellClassName || ""}`}
                        >
                          {column.render ? column.render(lead, { onOpenSummary: setSummaryLead }) : column.getValue(lead)}
                        </td>
                      ))}
                      <td className="sticky right-0 bg-white px-3 py-3 text-right dark:bg-slate-900">
                        <button
                          type="button"
                          onClick={() => onViewLead(lead)}
                          className="inline-flex h-8 items-center rounded-md border border-slate-200 px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-slate-900/10 dark:border-slate-700 dark:text-slate-100 dark:hover:bg-slate-800"
                        >
                          Просмотр
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Показано {filteredPageTotal} из {serverTotal} лидов.
        </p>
        <div className="flex flex-wrap items-center justify-end gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
            На странице
            <select
              value={limit}
              onChange={handleLimitChange}
              className="rounded-md border border-slate-200 px-2 py-1 text-xs shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Назад
          </button>
          <span className="text-xs text-slate-600 dark:text-slate-300">Стр. {currentPage} / {totalPages}</span>
          <button
            type="button"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= totalPages || loading}
            className="rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Далее
          </button>
        </div>
      </div>

      {renderFilterMenu()}

      {summaryLead ? (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-slate-900/50 p-4">
          <div className="w-full max-w-3xl rounded-lg border border-slate-200 bg-white shadow-2xl dark:border-slate-700 dark:bg-slate-800">
            <div className="flex items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 dark:border-slate-700">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Резюме</h3>
                <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
                  {summaryLead.name || "Без имени"} • {formatDate(summaryLead.datetime)}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSummaryLead(null)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-700"
              >
                Закрыть
              </button>
            </div>
            <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
              <p className="whitespace-pre-wrap text-sm leading-6 text-slate-800 dark:text-slate-100">
                {normalizeValue(summaryLead.summary_dialog)}
              </p>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default LeadsTable;
